/**
 * <LiveAccessDialog> — pourquoi le « GO ! » de l'élève ne l'a pas fait entrer (template).
 *
 * Le bouton de son prochain live presse toujours (`NextLiveSessionBanner.js:350-416`). Quand il
 * ne mène pas au live, il le dit, et c'est l'une de ces deux boîtes :
 *  - `not_today` — le live n'ouvre que le jour même : la boîte ANNONCE (un seul « OK ») et donne
 *    le jour où revenir. Le legacy la montrait avec Zoé ; le livre range le champion d'un dialogue
 *    dans ses « jamais », elle se lit donc sans lui ;
 *  - `payment_blocked` — l'abonnement n'est pas payé : c'est `<PaymentBlockedDialog>`, la boîte
 *    que l'élève lit aussi en touchant une séance suspendue.
 *
 * Contrôlée : `notice` arrive de l'écran, qui sait pourquoi la porte est fermée ; `null` = rien
 * d'ouvert. Les mots sont ceux du legacy.
 */
import React from "react"
import moment from "moment"

import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { type UILiveSession } from "../../../ports/primary/ui/types"
import { PaymentBlockedDialog } from "../PaymentBlockedDialog"

/** Ce qui retient l'élève à la porte de son live. */
export type LiveAccessNotice = "not_today" | "payment_blocked"

/** Par où la boîte s'est fermée : son bouton, ou en la congédiant. */
export type LiveAccessClose = "button" | "dismiss"

/** « jeudi » — le jour où revenir. À remonter dans `dateFormatter` au câblage. */
const weekday = (iso: string): string => moment(iso).locale("fr").format("dddd")

export type LiveAccessDialogProps = {
    /** La boîte ouverte — `null` : aucune. */
    notice: LiveAccessNotice | null
    /** Le live que l'élève voulait rejoindre : son jour s'écrit dans « Reviens jeudi… ». */
    session: Pick<UILiveSession, "date"> | null
    /**
     * La boîte se ferme. `how` dit par où, pour la mesure : `"button"` (« OK »), `"dismiss"` (la
     * croix, le fond, Échap). Les appelants qui ne mesurent rien l'ignorent.
     */
    onClose: (how?: LiveAccessClose) => void
    /** « Régulariser le paiement ». ABSENT = aucun lien à ouvrir : la boîte du paiement ne garde que « Fermer ». */
    onSettlePayment?: () => void
    testID?: string
}

export function LiveAccessDialog(props: LiveAccessDialogProps) {
    const { notice, session, onClose, onSettlePayment, testID } = props

    if (notice === "payment_blocked")
        return (
            <PaymentBlockedDialog visible onPay={onSettlePayment} onClose={() => onClose("dismiss")} testID={testID} />
        )

    return (
        <ConfirmDialog
            visible={notice === "not_today"}
            testID={testID}
            title="Le live n'est accessible que le jour même"
            message={session != null ? `Reviens ${weekday(session.date)} pour te connecter !` : undefined}
            confirmLabel="OK"
            danger={false}
            hideCancel
            onConfirm={() => onClose("button")}
            onCancel={() => onClose("dismiss")}
        />
    )
}
